import React, { useMemo, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Switch,
  IconButton,
  Box,
  RadioGroup,
  FormControlLabel,
  Radio,
  Autocomplete,
  InputAdornment,
} from "@mui/material";
import toast from "react-hot-toast";
import EditIcon from "@mui/icons-material/Edit";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import ExitToAppIcon from "@mui/icons-material/ExitToApp";
import Tooltip from "@mui/material/Tooltip";
import CloseIcon from "@mui/icons-material/Close";
import { axiosConfig } from "../../axios/axiosConfig";
import CancelIcon from "@mui/icons-material/Cancel";
import axios from "axios";
import CancelReasonPopover from "./CancelReasonPopover";
import {
  stopLossToPercent,
  percentToStopLoss,
  targetToProfitPercent,
  percentToTarget,
} from "../../utils/tradeCalculations";
const OptionTable = ({
  futureOptionData,
  futureOptionLoading,
  refetch,
  expiryDates,
  tradeFilter,
}) => {
  const showExitCols = tradeFilter !== "open";
  const [openTrades, setOpenTrades] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [editData, setEditData] = useState(null);
  const [exitOpen, setExitOpen] = useState(false);
  const [exitPrice, setExitPrice] = useState(null);
  const [exitRow, setExitRow] = useState("");
  const [loadingExit, setLoadingExit] = useState(false);
  const [sortDirection, setSortDirection] = useState("asc");
  const [sortBy, setSortBy] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    symbol: null,
    id: null,
    currentValue: null,
  });
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelRow, setCancelRow] = useState("");
  const [reason, setReason] = useState("");
  const [loadingCancel, setLoadingCancel] = useState(false);

  const options = [
    "Entry condition not met",
    "Trade is cancelled due to technical issue",
  ];

  const fmtDateTime = (val) => {
    if (!val) return "-";
    const d = new Date(val);
    if (isNaN(d)) return "-";
    const dd   = String(d.getDate()).padStart(2, "0");
    const mm   = String(d.getMonth() + 1).padStart(2, "0");
    const yyyy = d.getFullYear();
    const hh   = String(d.getHours()).padStart(2, "0");
    const min  = String(d.getMinutes()).padStart(2, "0");
    return `${dd}-${mm}-${yyyy} || ${hh}:${min}`;
  };

  const formatExpiry = (dateStr) => {
    if (!dateStr) return "";
    const d = new Date(dateStr);
    const day = String(d.getDate()).padStart(2, "0");
    const month = d.toLocaleDateString("en-US", { month: "short" }); // "Nov"
    return `${day} ${month}`;
  };

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(column);
      setSortDirection("asc");
    }
  };

  const sortedData = useMemo(() => {
    if (!futureOptionData) return [];

    const data = [...futureOptionData];

    const isOpen = (r) => r.trade_status === "OPEN";

    const compare = (a, b) => {
      const aOpen = isOpen(a);
      const bOpen = isOpen(b);

      // Keep OPEN trades above CLOSED ones
      if (aOpen && !bOpen) return -1;
      if (!aOpen && bOpen) return 1;

      // Continue sorting within both OPEN or both CLOSED
      if (!sortBy) return 0;

      let valA, valB;

      if (sortBy === "exit") {
        valA = a.closed_at
          ? new Date(a.closed_at).getTime()
          : sortDirection === "asc"
            ? Number.POSITIVE_INFINITY
            : Number.NEGATIVE_INFINITY;
        valB = b.closed_at
          ? new Date(b.closed_at).getTime()
          : sortDirection === "asc"
            ? Number.POSITIVE_INFINITY
            : Number.NEGATIVE_INFINITY;
      } else if (sortBy === "entry") {
        valA = a.date_of_recommendation
          ? new Date(a.date_of_recommendation).getTime()
          : sortDirection === "asc"
            ? Number.POSITIVE_INFINITY
            : Number.NEGATIVE_INFINITY;
        valB = b.date_of_recommendation
          ? new Date(b.date_of_recommendation).getTime()
          : sortDirection === "asc"
            ? Number.POSITIVE_INFINITY
            : Number.NEGATIVE_INFINITY;
      } else if (sortBy === "profit") {
        const aNum = parseFloat(a.profit_percent);
        const bNum = parseFloat(b.profit_percent);
        valA = isNaN(aNum)
          ? sortDirection === "asc"
            ? Number.POSITIVE_INFINITY
            : Number.NEGATIVE_INFINITY
          : aNum;
        valB = isNaN(bNum)
          ? sortDirection === "asc"
            ? Number.POSITIVE_INFINITY
            : Number.NEGATIVE_INFINITY
          : bNum;
      } else {
        return 0;
      }

      if (valA < valB) return sortDirection === "asc" ? -1 : 1;
      if (valA > valB) return sortDirection === "asc" ? 1 : -1;
      return 0;
    };

    return data.sort(compare);
  }, [futureOptionData, sortBy, sortDirection]);

  if (futureOptionLoading) {
    return (
      <div style={{ textAlign: "center", padding: "20px" }}>
        <CircularProgress />
      </div>
    );
  }

  if (!futureOptionData || futureOptionData.length === 0) {
    return (
      <Typography align="center" sx={{ mt: 3 }}>
        No data available
      </Typography>
    );
  }

  const handleOpenTrades = (row) => {
    setSelectedRow(row);
    setOpenTrades(true);
  };

  const handleCloseTrades = () => {
    setSelectedRow(null);
    setOpenTrades(false);
  };

  const handleOpenEdit = (row) => {
    const entryPrice = row?.trades?.[0]?.entry_price;
    setEditData({
      ...row,
      stop_loss_pct: stopLossToPercent(entryPrice, row.stop_loss),
      profit_pct: targetToProfitPercent(entryPrice, row.target_amount),
    });
    setEditOpen(true);
  };

  const handleCloseEdit = () => {
    setEditData(null);
    setEditOpen(false);
  };

  const handleSaveEdit = async () => {
    try {
      if (!editData) return;

      let payload = {};

      if (
        editData.trade_status === "INACTIVE" &&
        editData.trades &&
        editData.trades[0]
      ) {
        payload = {
          entry_price: editData.trades[0].entry_price,
        };
      } else {
        payload = {
          duration: editData.duration,
          stop_loss: editData.stop_loss,
          target_amount: editData.target_amount,
        };
      }

      const res = await axiosConfig({
        method: "put",
        url: `/option-future-trade/${editData.id}/`,
        data: payload,
      });
      toast.success("Trade updated successfully");
      setEditOpen(false);
      setEditData(null);
      if (refetch) refetch();
    } catch (error) {
      console.log(error, "error");
      const errMsg =
        error.response.data.response ||
        error.message ||
        "Failed to update trade";
      toast.error(errMsg);
    }
  };
  const handleToggleShowInApp = (row) => {
    setConfirmDialog({
      open: true,
      row: row,
    });
  };

  const handleConfirmToggle = async () => {
    try {
      const row = confirmDialog.row;
      const newValue = !row.show_in_app;

      const status = await axiosConfig({
        method: "put",
        url: `/option-future-trade/${row.id}/`,
        data: {
          show_in_app: newValue,
        },
      });

      toast.success(
        `${row.script_name || "Trade"} ${
          newValue ? "shown in" : "hidden from"
        } app successfully!`,
      );

      if (refetch) refetch();

      // Close dialog
      setConfirmDialog({ open: false, row: null });
    } catch (error) {
      const errMsg =
        error.response?.data?.response ||
        error.message ||
        "Failed to update show_in_app";
      toast.error(errMsg);
    }
  };
  const handleOpenExit = (row) => {
    setExitOpen(true);
    setExitRow(row);
    setExitPrice("");
  };

  const handleCloseExit = () => {
    setExitOpen(false);
    setExitRow(null);
    setExitPrice("");
  };
  const handleConfirmExit = async () => {
    const parentId = exitRow?.id;
    const trades = exitPrice?.[parentId];

    if (!parentId || !trades) {
      toast.error("Please enter exit price");
      return;
    }

    const firstTrade = Object.values(trades)[0];
    const { mode, value } = firstTrade || {};

    if (mode === "cmp") {
      if (!value || Number(value) === 0) {
        toast.error("CMP not available");
        return;
      }
    }

    if (mode === "textfield") {
      if (!value || value === "") {
        toast.error("Please enter exit price");
        return;
      }
    }

    try {
      setLoadingExit(true);
      await axiosConfig({
        method: "post",
        url: "/close-option-trade/",
        data: {
          id: parentId,
          exit_price: parseFloat(value),
        },
      });
      toast.success("Trade closed successfully");
      setExitOpen(false);
      setExitRow(null);
      setExitPrice({});
      if (refetch) refetch();
    } catch (error) {
      console.log(
        "Failed to close trade",
        error.response?.data || error.message,
      );
      toast.error(
        error.response?.data?.response ||
          error.message ||
          "Failed to close trade",
      );
    } finally {
      setLoadingExit(false);
    }
  };

  const handleCMPChange = async (parentRow, trade) => {
    try {
      const payload = {
        symbol: parentRow.symbol_name,
        expirytime: parentRow.expire_date,
      };
      const res = await axiosConfig({
        method: "post",
        url: "/stike-price-data/",
        data: payload,
      });

      const strikeData = res.data?.response?.strike_Data || [];
      const match = strikeData.find((item) => {
        return (
          parseFloat(item[parentRow.trades[0].option_type]?.striker_price) ===
          parseFloat(parentRow.trades[0].strike_price)
        );
      });

      if (!match) {
        toast.error("No matching strike found");
        return;
      }
      let latestLTP =
        parentRow.trades[0].option_type === "CE"
          ? match.CE?.ltp
          : match.PE?.ltp;

      if (latestLTP == null) {
        toast.error(`No LTP available for ${trade.option_type}`);
        return;
      }

      setExitPrice((prev) => ({
        ...prev,
        [parentRow.id]: {
          ...(prev[parentRow.id] || {}),
          [trade.id]: { mode: "cmp", value: latestLTP },
        },
      }));
    } catch (error) {
      console.error("CMP API error:", error.response?.data || error.message);
      toast.error("CMP API error:", error.response?.data || error.message);
    }
  };

  const handleOpenCancel = (row) => {
    setCancelOpen(true);
    setCancelRow(row);
  };

  const handleCloseCancel = () => {
    setCancelOpen(false);
    setCancelRow("");
  };
  const handleConfirmCancel = async () => {
    if (!reason) {
      toast.error("Please enter or select a cancel reason.");
      return;
    }
    try {
      setLoadingCancel(true);
      const res = await axiosConfig({
        method: "post",
        url: "/cancel-option-trade/",
        data: {
          trade_id: cancelRow.id,
          trade_status: "cancel",
          cancel_reason: reason,
        },
      });
      const successMsg = res.data.response || "Trade cancelled successfully";
      toast.success(successMsg);

      setCancelOpen(false);
      setCancelRow(null);
      if (refetch) refetch();
    } catch (error) {
      const errMsg =
        error.response.data.response ||
        error.message ||
        "Failed to Cancel Trade.";
      toast.error(errMsg);
    } finally {
      setLoadingCancel(false);
    }
  };
  return (
    <>
      <TableContainer
        component={Paper}
        sx={{
          borderRadius: 0,
          boxShadow: "none",
          width: "100%",
          overflowX: "auto",
        }}
      >
        <Table
          stickyHeader
          size="small"
          sx={{
            width: "100%",
            tableLayout: "fixed",
            "& .MuiTableCell-root": {
              padding: "4px 4px",
              fontSize: "10px",
              borderBottom: "1px solid #f0f4f8",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            },
            "& .MuiTableBody-root .MuiTableRow-root": {
              height: "36px",
              "&:hover": { background: "#f0f7ff" },
              "&:nth-of-type(even)": { background: "#f8fafc" },
              "&:nth-of-type(even):hover": { background: "#e8f4ff" },
            },
          }}
        >
          <TableHead>
            <TableRow
              sx={{
                "& th": {
                  fontWeight: "700",
                  fontSize: "10px",
                  color: "#ffffff",
                  background: "#04343a",
                  textTransform: "uppercase",
                  letterSpacing: "0.4px",
                  whiteSpace: "nowrap",
                  textAlign: "center",
                  lineHeight: 1.2,
                  padding: "8px 6px",
                  borderBottom: "none",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                },
              }}
            >
              <TableCell sx={{ width: "3%" }}>S.No</TableCell>
              <TableCell
                onClick={() => handleSort("entry")}
                sx={{ cursor: "pointer", userSelect: "none", width: "5%" }}
              >
                Entry {sortDirection === "asc" ? "↑" : "↓"}
              </TableCell>
              {tradeFilter !== "close" && <TableCell sx={{ width: "3%" }}>Inst. Type</TableCell>}
              <TableCell sx={{ width: "10%" }}>Trade Symbol</TableCell>
              <TableCell sx={{ width: "4%" }}>Show App</TableCell>
              <TableCell sx={{ width: "3%" }}>Order</TableCell>
              <TableCell sx={{ width: "5%" }}>Entry ₹</TableCell>
              <TableCell sx={{ width: "5%" }}>Target ₹</TableCell>
              <TableCell sx={{ width: "4%" }}>SL</TableCell>
              {showExitCols && <TableCell sx={{ width: "4%" }}>Exit ₹</TableCell>}
              <TableCell sx={{ width: "5%" }}>Profit ₹</TableCell>
              <TableCell
                onClick={() => handleSort("profit")}
                sx={{ cursor: "pointer", userSelect: "none", width: "5%" }}
              >
                P&L {sortDirection === "asc" ? "↑" : "↓"}
              </TableCell>
              {tradeFilter !== "close" && <TableCell sx={{ width: "4%" }}>LTP</TableCell>}
              {tradeFilter === "close" && (
                <>
                  <TableCell sx={{ width: "5%" }}>Price Selection</TableCell>
                  <TableCell sx={{ width: "4%" }}>Bid Type</TableCell>
                </>
              )}
              {(!showExitCols || tradeFilter === "inactive") && (
                <TableCell align="center" sx={{ width: "10%" }}>
                  Actions
                </TableCell>
              )}
              <TableCell sx={{ width: "5%" }}>Created By</TableCell>
              <TableCell
                onClick={() => handleSort("entry")}
                sx={{ cursor: "pointer", userSelect: "none", width: "7%" }}
              >
                Created Date {sortDirection === "asc" ? "↑" : "↓"}
              </TableCell>
              {showExitCols && (
                <TableCell
                  onClick={() => handleSort("exit")}
                  sx={{ cursor: "pointer", userSelect: "none", width: "7%" }}
                >
                  Exit Date {sortDirection === "asc" ? "↑" : "↓"}
                </TableCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedData &&
              sortedData.map((row, index) => {
                return (
                  <TableRow key={row.id}>
                    <TableCell
                      align="center"
                      sx={{ color: "#9ca3af", fontSize: "11px" }}
                    >
                      {index + 1}
                    </TableCell>
                    <TableCell align="center">
                      {" "}
                      {new Date(row.date_of_recommendation).toLocaleDateString(
                        "en-GB",
                      )}
                    </TableCell>
                    {tradeFilter !== "close" && (
                      <TableCell align="center">
                        {row.instrument_type_name}
                      </TableCell>
                    )}
                    <TableCell align="center">
                      {row?.trade_symbol ?? "-"}
                    </TableCell>
                    <TableCell align="center">
                      <Switch
                        checked={row.show_in_app}
                        onChange={() => {
                          if (row.trade_status === "CANCEL") {
                            if (!row.show_in_app) {
                              toast.error(
                                "Cancelled trade cannot be shown in app",
                              );
                              return;
                            }
                          }
                          handleToggleShowInApp(row);
                        }}
                        color="success"
                        // disabled={row.trade_status === "CANCEL"}
                      />
                    </TableCell>
                    <TableCell align="center">
                      {row.trades?.[0]?.order_type ?? "-"}
                    </TableCell>
                    <TableCell align="center">
                      {row.trades?.[0]?.entry_price ?? "-"}
                    </TableCell>
                    <TableCell align="center">{row.target_amount || "-"}</TableCell>
                    <TableCell align="center">{row.stop_loss || "-"}</TableCell>
                    {showExitCols && (
                      <TableCell align="center">
                        {row.close_price || "-"}
                      </TableCell>
                    )}
                    <TableCell
                      align="center"
                      sx={{
                        color: row.profit_amount < 0 ? "red" : "green",
                        fontWeight: "bold",
                      }}
                    >
                      {row.profit_amount ?? "-"}
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{
                        color: row.profit_percent < 0 ? "red" : "green",
                        fontWeight: "bold",
                      }}
                    >
                      {row.profit_percent != null
                        ? `${Number(row.profit_percent).toFixed(2)}%`
                        : "-"}
                    </TableCell>
                    {tradeFilter !== "close" && (
                      <TableCell align="center">
                        {row.trades?.[0]?.last_trading_price ?? "-"}
                      </TableCell>
                    )}
                    {tradeFilter === "close" && (
                      <>
                        <TableCell align="center">
                          {row.trades?.[0]?.price_selection_type || "-"}
                        </TableCell>
                        <TableCell align="center">
                          {row.trades?.[0]?.bid_type || "-"}
                        </TableCell>
                      </>
                    )}

                    {(!showExitCols || tradeFilter === "inactive") && <TableCell align="center" sx={{ px: 0 }}>
                      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", gap: "0.3rem" }}>
                        <Tooltip title="View Trades" placement="top">
                          <button
                            onClick={() => handleOpenTrades(row)}
                            style={{ padding: "3px 7px", cursor: "pointer", background: "#0284c7", color: "#fff", border: "none", borderRadius: 4, transition: "background 0.2s", display: "inline-flex", alignItems: "center", fontSize: 12 }}
                          >
                            <ReceiptLongIcon sx={{ fontSize: 14 }} />
                          </button>
                        </Tooltip>
                        {row.trade_status === "CANCEL" && (
                          <CancelReasonPopover reason={row.cancel_reason} />
                        )}
                        {row.trade_status !== "CLOSE" &&
                          row.trade_status !== "CANCEL" && (
                            <Tooltip title="Edit Trade" placement="top">
                              <button
                                onClick={() => handleOpenEdit(row)}
                                style={{ padding: "3px 7px", cursor: "pointer", background: "#016551", color: "#fff", border: "none", borderRadius: 4, transition: "background 0.2s", display: "inline-flex", alignItems: "center", fontSize: 12 }}
                              >
                                <EditIcon sx={{ fontSize: 14 }} />
                              </button>
                            </Tooltip>
                          )}
                        {row.trade_status !== "CLOSE" &&
                          row.trade_status !== "CANCEL" && (
                            <Tooltip title="Cancel Trade" placement="top">
                              <button
                                onClick={() => handleOpenCancel(row)}
                                style={{ padding: "3px 7px", cursor: "pointer", background: "#d97706", color: "#fff", border: "none", borderRadius: 4, transition: "background 0.2s", display: "inline-flex", alignItems: "center", fontSize: 12 }}
                              >
                                <CancelIcon sx={{ fontSize: 14 }} />
                              </button>
                            </Tooltip>
                          )}
                        {row.trade_status !== "CLOSE" &&
                          row.trade_status !== "CANCEL" &&
                          row.trade_status !== "INACTIVE" && (
                            <Tooltip title="Exit Trade" placement="top">
                              <button
                                onClick={() => handleOpenExit(row)}
                                style={{ padding: "3px 7px", cursor: "pointer", background: "#dc2626", color: "#fff", border: "none", borderRadius: 4, transition: "background 0.2s", display: "inline-flex", alignItems: "center", fontSize: 12 }}
                              >
                                <ExitToAppIcon sx={{ fontSize: 14 }} />
                              </button>
                            </Tooltip>
                          )}
                      </Box>
                    </TableCell>}
                    <TableCell align="center">
                      {row.created_by ?? "-"}
                    </TableCell>
                    <TableCell align="center">
                      {fmtDateTime(row.date_of_recommendation)}
                    </TableCell>
                    {showExitCols && (
                      <TableCell align="center">
                        {fmtDateTime(row.closed_at)}
                      </TableCell>
                    )}
                  </TableRow>
                );
              })}
          </TableBody>
        </Table>
      </TableContainer>
      {/* Trades Modal */}
      <Dialog
        open={openTrades}
        onClose={handleCloseTrades}
        maxWidth="lg"
        fullWidth
        PaperProps={{ sx: { borderRadius: "12px", overflow: "hidden" } }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            px: 3,
            py: 2,
            background: "#04343a",
          }}
        >
          <Typography variant="h6" fontWeight="700" sx={{ color: "#fff" }}>
            Trades
          </Typography>
          <IconButton
            onClick={handleCloseTrades}
            size="small"
            sx={{
              color: "rgba(255,255,255,0.7)",
              "&:hover": { color: "#fff", background: "rgba(255,255,255,0.1)" },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
        <DialogContent sx={{ p: 2 }}>
          <Table size="small">
            <TableHead>
              <TableRow
                sx={{
                  "& th": {
                    background: "#f1f5f9",
                    fontWeight: "700",
                    fontSize: "11px",
                    color: "#475569",
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                  },
                }}
              >
                <TableCell sx={{ fontWeight: "bold" }}>Strike Price</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>Order Type</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>Option Type</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>Entry Price</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>Expiry Date</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {selectedRow?.trades?.map((t) => (
                <TableRow key={t.id}>
                  <TableCell>{t.strike_price}</TableCell>
                  <TableCell>{t.order_type}</TableCell>
                  <TableCell>{t.option_type}</TableCell>
                  <TableCell>{t.entry_price || "-"}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </DialogContent>
      </Dialog>
      {/* Edit Modal */}
      <Dialog
        open={editOpen}
        onClose={handleCloseEdit}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: "12px", overflow: "hidden" } }}
      >
        <Box
          sx={{
            px: 3,
            py: 2,
            background: "#04343a",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography variant="h6" fontWeight="700" sx={{ color: "#fff" }}>
            Edit Trade
          </Typography>
          <IconButton
            onClick={handleCloseEdit}
            size="small"
            sx={{
              color: "rgba(255,255,255,0.7)",
              "&:hover": { color: "#fff", background: "rgba(255,255,255,0.1)" },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
        <DialogContent>
          <TextField
            label="Symbol Name"
            fullWidth
            margin="dense"
            value={editData?.symbol_name ?? ""}
            onChange={(e) =>
              setEditData({ ...editData, symbol_name: e.target.value })
            }
            InputProps={{ readOnly: true }}
          />
          <TextField
            label="Duration"
            fullWidth
            margin="dense"
            value={editData?.duration ?? ""}
            onChange={(e) =>
              editData?.trade_status !== "INACTIVE" &&
              setEditData({ ...editData, duration: e.target.value })
            }
            InputProps={{
              readOnly: editData?.trade_status === "INACTIVE",
            }}
          />

          <TextField
            label="Lot Size"
            fullWidth
            margin="dense"
            value={editData?.lot_size ?? ""}
            onChange={(e) =>
              setEditData({ ...editData, lot_size: e.target.value })
            }
            InputProps={{ readOnly: true }}
          />
          <TextField
            label="Entry Price"
            fullWidth
            margin="dense"
            value={editData?.trades?.[0]?.entry_price ?? ""}
            onChange={(e) =>
              editData?.trade_status === "INACTIVE"
                ? setEditData({
                    ...editData,
                    trades: [
                      {
                        ...editData.trades[0],
                        entry_price: e.target.value,
                      },
                    ],
                  })
                : null
            }
            InputProps={{
              readOnly: editData?.trade_status !== "INACTIVE",
            }}
          />

          <TextField
            label="CMP"
            fullWidth
            margin="dense"
            value={editData?.trades?.[0]?.last_trading_price ?? ""}
            InputProps={{ readOnly: true }}
          />
          {/* Stop Loss — amount ↔ % */}
          <Box sx={{ display: "flex", gap: 1 }}>
            <TextField
              label="Stop Loss (Amount)"
              fullWidth
              margin="dense"
              value={editData?.stop_loss ?? ""}
              onChange={(e) => {
                if (editData?.trade_status === "INACTIVE") return;
                const val = e.target.value;
                const entry = editData?.trades?.[0]?.entry_price;
                setEditData({
                  ...editData,
                  stop_loss: val,
                  stop_loss_pct: stopLossToPercent(entry, val),
                });
              }}
              InputProps={{ readOnly: editData?.trade_status === "INACTIVE" }}
            />
            <TextField
              label="Stop Loss %"
              fullWidth
              margin="dense"
              type="number"
              value={editData?.stop_loss_pct ?? ""}
              onChange={(e) => {
                if (editData?.trade_status === "INACTIVE") return;
                const pct = e.target.value;
                const entry = editData?.trades?.[0]?.entry_price;
                const orderType = editData?.trades?.[0]?.order_type;
                setEditData({
                  ...editData,
                  stop_loss_pct: pct,
                  stop_loss: percentToStopLoss(entry, pct, orderType),
                });
              }}
              InputProps={{
                readOnly: editData?.trade_status === "INACTIVE",
                endAdornment: <InputAdornment position="end">%</InputAdornment>,
              }}
            />
          </Box>

          {/* Target Price — amount ↔ profit % */}
          <Box sx={{ display: "flex", gap: 1 }}>
            <TextField
              label="Target Price (Amount)"
              fullWidth
              margin="dense"
              value={editData?.target_amount ?? ""}
              onChange={(e) => {
                if (editData?.trade_status === "INACTIVE") return;
                const val = e.target.value;
                const entry = editData?.trades?.[0]?.entry_price;
                setEditData({
                  ...editData,
                  target_amount: val,
                  profit_pct: targetToProfitPercent(entry, val),
                });
              }}
              InputProps={{ readOnly: editData?.trade_status === "INACTIVE" }}
            />
            <TextField
              label="Profit %"
              fullWidth
              margin="dense"
              type="number"
              value={editData?.profit_pct ?? ""}
              onChange={(e) => {
                if (editData?.trade_status === "INACTIVE") return;
                const pct = e.target.value;
                const entry = editData?.trades?.[0]?.entry_price;
                const orderType = editData?.trades?.[0]?.order_type;
                setEditData({
                  ...editData,
                  profit_pct: pct,
                  target_amount: percentToTarget(entry, pct, orderType),
                });
              }}
              InputProps={{
                readOnly: editData?.trade_status === "INACTIVE",
                endAdornment: <InputAdornment position="end">%</InputAdornment>,
              }}
            />
          </Box>

          <TextField
            label="Expiry Date"
            type="date"
            fullWidth
            margin="dense"
            value={editData?.expire_date ?? ""}
            onChange={(e) =>
              setEditData({ ...editData, expire_date: e.target.value })
            }
            InputLabelProps={{ shrink: true }}
            InputProps={{ readOnly: true }}
          />
        </DialogContent>
        <DialogActions
          sx={{ px: 3, py: 2, borderTop: "1px solid #e2e8f0", gap: 1 }}
        >
          <Button
            onClick={handleCloseEdit}
            variant="outlined"
            sx={{
              borderRadius: "7px",
              fontWeight: 600,
              color: "#64748b",
              borderColor: "#cbd5e1",
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSaveEdit}
            variant="contained"
            sx={{
              borderRadius: "7px",
              fontWeight: 700,
              background: "linear-gradient(135deg, #16a34a, #15803d)",
              "&:hover": {
                background: "linear-gradient(135deg, #15803d, #166534)",
              },
            }}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
      {/* Exit Modal */}
      <Dialog
        open={exitOpen}
        onClose={handleCloseExit}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: "12px", overflow: "hidden" } }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            px: 3,
            py: 2,
            background: "#04343a",
          }}
        >
          <Typography variant="h6" fontWeight="700" sx={{ color: "#fff" }}>
            {exitRow?.symbol_name} — Exit Price
          </Typography>
          <IconButton
            onClick={handleCloseExit}
            size="small"
            sx={{
              color: "rgba(255,255,255,0.7)",
              "&:hover": { color: "#fff", background: "rgba(255,255,255,0.1)" },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        <DialogContent>
          {exitRow?.trades?.map((t) => {
            return (
              <Box
                key={t.id}
                sx={{
                  border: "1px solid #ddd",
                  borderRadius: 2,
                  p: 2,
                  mb: 2,
                }}
              >
                <Typography
                  variant="subtitle2"
                  sx={{ mb: 1 }}
                  fontWeight={"bold"}
                >
                  Strike Price: {t.strike_price} || Target Price:
                  {exitRow.target_amount} || Option: {t.option_type} || LTP:
                  {t.last_trading_price} || Entry Price :{t.entry_price}
                </Typography>

                <RadioGroup
                  row
                  value={exitPrice?.[exitRow.id]?.[t.id]?.mode || "textfield"}
                  onChange={(e) => {
                    const mode = e.target.value;

                    if (mode === "cmp") {
                      setExitPrice((prev) => ({
                        ...prev,
                        [exitRow.id]: {
                          ...(prev[exitRow.id] || {}),
                          [t.id]: { mode: "cmp", value: "" },
                        },
                      }));
                      handleCMPChange(exitRow, t);
                    }

                    setExitPrice((prev) => ({
                      ...prev,
                      [exitRow.id]: {
                        ...(prev[exitRow.id] || {}),
                        [t.id]:
                          mode === "cmp"
                            ? {
                                mode: "cmp",
                                value: prev[exitRow.id]?.[t.id]?.value || "",
                              }
                            : {
                                mode: "textfield",
                                value: prev[exitRow.id]?.[t.id]?.value || "",
                              },
                      },
                    }));
                  }}
                >
                  <FormControlLabel
                    value="textfield"
                    control={<Radio />}
                    label="Enter Price"
                  />
                  <FormControlLabel
                    value="cmp"
                    control={<Radio />}
                    label={`CMP (${
                      exitPrice?.[exitRow.id]?.[t.id]?.mode === "cmp"
                        ? (exitPrice[exitRow.id][t.id]?.value ?? "-")
                        : "-"
                    })`}
                  />
                </RadioGroup>

                {exitPrice?.[exitRow.id]?.[t.id]?.mode !== "cmp" && (
                  <TextField
                    label="Exit Price"
                    type="number"
                    fullWidth
                    margin="dense"
                    value={exitPrice?.[exitRow.id]?.[t.id]?.value || ""}
                    onChange={(e) =>
                      setExitPrice((prev) => ({
                        ...prev,
                        [exitRow.id]: {
                          ...(prev[exitRow.id] || {}),
                          [t.id]: {
                            mode: "textfield",
                            value: e.target.value,
                          },
                        },
                      }))
                    }
                  />
                )}

                {(() => {
                  const currentExitVal = exitPrice?.[exitRow.id]?.[t.id]?.value;
                  const exitNum = parseFloat(currentExitVal);
                  const entryNum = parseFloat(t.entry_price);
                  const lotNum = parseFloat(exitRow.lot_size) || 1;
                  const orderType = t.order_type || "BUY";

                  if (!currentExitVal || isNaN(exitNum) || isNaN(entryNum)) return null;

                  const diff = orderType === "SELL" ? entryNum - exitNum : exitNum - entryNum;
                  const profitAmt = (diff * lotNum).toFixed(2);
                  const profitPct = ((diff / entryNum) * 100).toFixed(2);
                  const isPositive = diff >= 0;

                  return (
                    <Box
                      sx={{
                        mt: 1,
                        p: 1.5,
                        borderRadius: "8px",
                        background: isPositive ? "#f0fdf4" : "#fef2f2",
                        border: `1px solid ${isPositive ? "#bbf7d0" : "#fecaca"}`,
                        display: "flex",
                        gap: 3,
                      }}
                    >
                      <Box>
                        <Typography variant="caption" sx={{ color: "#64748b", display: "block" }}>
                          Profit ₹
                        </Typography>
                        <Typography
                          fontWeight="700"
                          sx={{ color: isPositive ? "#16a34a" : "#dc2626", fontSize: "15px" }}
                        >
                          {isPositive ? "+" : ""}{profitAmt}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" sx={{ color: "#64748b", display: "block" }}>
                          Profit %
                        </Typography>
                        <Typography
                          fontWeight="700"
                          sx={{ color: isPositive ? "#16a34a" : "#dc2626", fontSize: "15px" }}
                        >
                          {isPositive ? "+" : ""}{profitPct}%
                        </Typography>
                      </Box>
                    </Box>
                  );
                })()}
              </Box>
            );
          })}
        </DialogContent>

        <DialogActions>
          <Button
            onClick={handleConfirmExit}
            color="success"
            variant="contained"
            disabled={loadingExit}
          >
            {loadingExit ? <CircularProgress size={20} /> : "Confirm Exit"}
          </Button>
        </DialogActions>
      </Dialog>
      {/* Add this Dialog component at the end (before closing return statement) */}
      <Dialog
        open={confirmDialog.open}
        onClose={() => setConfirmDialog({ open: false, row: null })}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: "bold", textAlign: "center" }}>
          Confirm Action
        </DialogTitle>
        <DialogContent sx={{ textAlign: "center", py: 3 }}>
          <Typography variant="body1">
            Are you sure you want to{" "}
            <strong
              style={{
                color: confirmDialog.row?.show_in_app ? "#d32f2f" : "#2e7d32",
              }}
            >
              {confirmDialog.row?.show_in_app ? "hide" : "show"}
            </strong>{" "}
            <strong style={{ color: "#1976d2" }}>
              {confirmDialog.row?.symbol_name || "this trade"}
            </strong>{" "}
            in app?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ justifyContent: "center", pb: 2, gap: 1 }}>
          <Button
            onClick={() => setConfirmDialog({ open: false, row: null })}
            variant="outlined"
            color="inherit"
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmToggle}
            variant="contained"
            color="success"
          >
            Yes, Confirm
          </Button>
        </DialogActions>
      </Dialog>
      {/* cancel trade dialogue box */}
      <Dialog
        open={cancelOpen}
        onClose={handleCloseCancel}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: "12px", overflow: "hidden" } }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            px: 3,
            py: 2,
            background: "linear-gradient(135deg, #7f1d1d 0%, #991b1b 100%)",
          }}
        >
          <Box>
            <Typography
              variant="h6"
              fontWeight="700"
              sx={{ color: "#fff", fontSize: "15px" }}
            >
              Cancel Trade
            </Typography>
            {cancelRow?.trades?.[0] && (
              <Typography
                variant="caption"
                sx={{ color: "rgba(255,255,255,0.7)" }}
              >
                {cancelRow.trades[0].order_type} ·{" "}
                {formatExpiry(cancelRow.expire_date)} ·{" "}
                {Number(cancelRow.trades[0].strike_price)}{" "}
                {cancelRow.trades[0].option_type}
              </Typography>
            )}
          </Box>
          <IconButton
            onClick={handleCloseCancel}
            size="small"
            sx={{
              color: "rgba(255,255,255,0.7)",
              "&:hover": { color: "#fff", background: "rgba(255,255,255,0.1)" },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        <DialogContent sx={{ py: 2 }}>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Are you sure you want to cancel{" "}
            <strong>
              {cancelRow?.trades?.[0]
                ? `${cancelRow.trades[0].order_type} ${formatExpiry(
                    cancelRow.expire_date,
                  )} ${Number(cancelRow.trades[0].strike_price)} ${
                    cancelRow.trades[0].option_type
                  }`
                : ""}
            </strong>{" "}
            trade? Please select a reason below:
          </Typography>

          <Autocomplete
            freeSolo
            options={options} // existing options
            value={reason}
            onChange={(_e, newValue) => setReason(newValue || "")}
            onInputChange={(_e, newInputValue) => setReason(newInputValue)}
            renderInput={(params) => (
              <TextField
                {...params}
                label={
                  !options.includes(reason) && reason
                    ? "Adding Custom Reason" // Visual cue
                    : "Select or type reason"
                }
                fullWidth
                placeholder="Select from list or type custom"
              />
            )}
          />

          {/* Optional: Show a clear "Custom" tag below input if value not in list */}
          {reason && !options.includes(reason) && (
            <Box sx={{ mt: 1 }}>
              <Typography variant="caption" color="primary">
                You are adding a custom reason
              </Typography>
            </Box>
          )}
        </DialogContent>

        <DialogActions>
          <Button
            onClick={handleConfirmCancel}
            variant="contained"
            color="warning"
            disabled={loadingCancel}
          >
            {loadingCancel ? <CircularProgress size={20} /> : "Confirm Cancel"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default OptionTable;
